import React from 'react';
import { useForm, Form, SonForm } from '../../../../_lib/Form2';
import FormSubmitOrCancel from '../../../molecules/Form/components/FormSubmitOrCancel/FormSubmitOrCancel';
import { AnyParameter } from '../../../../_lib/Form2/types/SonForm';

type FormWithActions = SonForm<AnyParameter> & {
  actions?: {
    accept?: string;
    reject?: string;
  };
};

type TeamroomInvitationViewProps = {
  form: FormWithActions;
  headline: string;
  messageSuccess: string;
  messageFail: string;
  messageRejectSuccess: string;
  messageRejectFail: string;
  messageSending: string;
};

type FetchStateWithAction = {
  type: string;
  currentAction?: string;
  requestError?: unknown;
};

function mapFetchStateToStatus(fetchState: FetchStateWithAction): '' | 'success' | 'fail' | 'sending' {
  if (fetchState.type === 'success') {
    return 'success';
  }
  if (fetchState.type === 'error') {
    return 'fail';
  }
  if (fetchState.type === 'loading') {
    return 'sending';
  }

  return '';
}

function getErrorMessage(fetchState: FetchStateWithAction): string | undefined {
  if (fetchState.type === 'error' && fetchState.requestError) {
    const error = fetchState.requestError as { message?: string; data?: { messages?: string[] } };
    return error.data?.messages?.[0] || error.message || 'Ein Fehler ist aufgetreten.';
  }

  return undefined;
}

function isRejectAction(fetchState: FetchStateWithAction): boolean {
  if (!fetchState.currentAction) {
    return false;
  }

  return fetchState.currentAction.includes('teamroomInvitationReject');
}

export function TeamroomInvitationView({
  form,
  headline,
  messageSuccess,
  messageFail,
  messageRejectSuccess,
  messageRejectFail,
  messageSending,
}: TeamroomInvitationViewProps) {
  const { fetchState, params, formProps } = useForm<SonForm<AnyParameter>>({
    form,
    onSuccess: () => {},
  });

  const status: '' | 'success' | 'fail' | 'sending' = mapFetchStateToStatus(fetchState);

  const getStatusText = () => {
    const rejectAction = isRejectAction(fetchState);

    if (status === 'success') {
      return rejectAction ? messageRejectSuccess : messageSuccess;
    }
    if (status === 'fail') {
      return rejectAction ? messageRejectFail : messageFail;
    }
    if (status === 'sending') {
      return messageSending;
    }

    return '';
  };

  const errorMessage = getErrorMessage(fetchState);
  const interactionStatusText = errorMessage || getStatusText();

  const hiddenParams = params.getAllByType('hidden').filter((param) => {
    if (Array.isArray(param.value) && param.value.length === 0) {
      return false;
    }

    return param.value != null && param.value !== '';
  });

  return (
    <Form {...formProps}>
      {hiddenParams.map(param => (
        <input key={param.value} type="hidden" name={param.name} value={param.value} />
      ))}
      <FormSubmitOrCancel
        interactionStatusText={interactionStatusText}
        status={status}
        statusModifier="right"
        headline={headline}
        hierarchy={3}
        acceptButtonType="submit"
        acceptFormAction={form.actions?.accept}
        cancelButtonType="submit"
        cancelFormAction={form.actions?.reject}
        hideSubmitButton={false}
      />
    </Form>
  );
}
